import ModuleCardMenusNew from './ModuleCardMenusNew';
import { useDispatch } from 'react-redux';
import { toggleModal } from '../../reducers/modalReducer';
import { showMessage } from '../../config';
import ModuleCardHeader from './moduleFace/ModuleCardHeader';
import ModuleCardDescription from './moduleFace/ModuleCardDescription';
import ModuleCardStats from './moduleFace/ModuleCardStats';
import CertificationBadge from './moduleFace/CertificationBadge';
import PassFailBadge from './moduleFace/PassFailBadge';
import ModuleCardActions from './moduleFace/ModuleCardActions';
import { useNavigate } from 'react-router-dom';

const DURATION_LABELS = {
    'isReady': 'Is Ready',
    '1 mins': '1 Min',
    '2 mins': '2 Mins',
    '3mins': '3 Mins',
    '5mins': '5 Mins',
    '10mins': '10 Mins',
    '15mins': '15 Mins',
    '30mins': '30 Mins',
    '2 days': '2 Days',
};

const getModuleUrl = (moduleName) => {
    return moduleName.trim().toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-');
}

function ModuleCard({
    name = 'Module Name',
    subTitle = '',
    logoAlt = 'Module Logo',
    description,
    module,
    user,
    permissionLevel,
    moduleCertification,
    getFitInvestorFirms,
    type,
    setTab,
    company,
    setIsWorkflowReport,
    seeMore = null,
    howItWorks = null,
    imageURL,
    showPercentages,
    setCompanyDashboardTab,
    menus,
    reportMenu
}) {
    const defaultLogo = require('../../../images/icon.png');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const resolvedLogo = imageURL || module?.imageURL || defaultLogo;
    const moduleSlug = module?.slug?.includes('business-audit') ? `${module?.slug}-${company?.['testType']}` : module?.slug || type || getModuleUrl(name);
    const descriptionText = description ?? module?.moduleDescription ?? '';

    // ── Derived state from module fields ─────────────────────────────────────
    const isStarted = module?.isStarted || false;
    const completenessPercent = module?.completenessPercent || 0;
    const riskPercent = module?.riskPercent || 0;
    const sectionRestriction = module?.sectionAccessRestrictions?.filter(r => r?.companyObjective?.toLowerCase() === company?.testType?.toLowerCase() && r?.subscriptionTier?.toLowerCase() === company?.subscription?.toLowerCase())[0]?.isRestricted || false;
    const isRestricted = sectionRestriction || module?.isRestricted === true;
    const isPaid = module?.isPaid || false;
    const hasBoughtExpension = Array.isArray(company?.boughtExpensions) &&
        company.boughtExpensions.some(id => id?.toString() === module?._id?.toString());
    const hasPurchased = !isPaid || module?.hasPurchased === true || hasBoughtExpension;

    const showRestrictedMessage = () => {
        showMessage('This module is restricted for your subscription. You need to upgrade.');
    };

    const isRestrictedBlocked = () => {
        if (!isRestricted) return false;
        showRestrictedMessage();
        return true;
    };

    const openPaidModulePurchaseModal = () => {
        if (isRestrictedBlocked()) return;
        dispatch(toggleModal({
            open: 'subscriptionPaymentConfirm',
            purchasePlan: 'tempModulePurchase',
            purchaseDefaultStep: 'tempModulePurchase',
            modulePurchase: {
                tempModuleId: module?._id,
                moduleName: module?.moduleName || name,
                price: module?.price,
                subscriptionTier: company?.subscription,
                companyObjective: company?.testType,
                stripeProductPriceId: module?.stripeProductPriceId,
            },
        }));
    };

    const setTabWithRestrictionCheck = async (nextTab) => {
        if (isRestrictedBlocked()) return;
        if (module?.isDirectorsModule) {
            dispatch(toggleModal({ open: 'showTempDirectors', tempDirectorsModuleSlug: module.slug || moduleSlug }));
            return;
        }
        if (module?.isNavigationModule) {
            setCompanyDashboardTab(nextTab.includes('#') ? nextTab.replace('#', '') : `${nextTab}`)
        } else {
            return setTab(nextTab);
        }
    };
    const durationLabel = module?.duration && module.duration !== 'NIL'
        ? (DURATION_LABELS[module.duration] || null)
        : null;

    const showStats = isStarted || hasBoughtExpension;
    const showCompleteness = showStats && module?.completeness === 'Yes';
    const showRisk = showStats && module?.risks === 'Yes';
    const showCertification = showStats && module?.certification === 'Yes';
    const showPassFail = showStats && module?.passFail === 'Yes';

    const subscriptionLabel = (() => {
        const regex = /(audit|repository|anylist|Workflow)/i;

        if (regex.test(module?.moduleName)) {
            return company?.subscription?.toUpperCase();
        }
        return null
    })();

    const hasPassed = completenessPercent >= 70;

    const navigateSection = async (type, sectionNo) => {
        if (isPaid && !hasPurchased) {
            openPaidModulePurchaseModal();
            return;
        }
        if (user?.['role'] == 'Guest') {
            dispatch(toggleModal({ open: 'noRegistrationReport' }));
            return;
        }
        if ((user['role'] == "Advisor" || user['role'] == "Investor") && (permissionLevel == 'DASHBOARD' || permissionLevel == 'DASHBOARD & REPORT')) {
            showMessage(`You have ${permissionLevel} access only!`);
            return;
        }
        if (['software-development', 'esg', 'warranty-disclosure', 'directors-questionnaire'].includes(type)) {
            const message = `You don't have permission for this module. Please contact your admin ${company['creatorId']['firstName'] ? `${company['creatorId']['firstName']} ${company['creatorId']['lastName'] ? company['creatorId']['lastName'] : ""}` : 'user'} to gain access`;
            if (user['role'] == 'Member' && company['creatorId']['_id'] != user['_id']) {
                if (!(company?.['memberPermissions']?.[user['_id']]?.[module?.['sections']?.[0]?.['sectionNo']])) {
                    showMessage(message);
                    return;
                }
            }
            if ((user['role'] == "Advisor" || user['role'] == 'Investor') && company['creatorId']['_id'] != user['_id']) {
                if (!(company?.[user['role'] == "Investor" ? 'investors' : 'advisors']?.[user['_id']]?.['modules']?.[module?.['sections']?.[0]?.['sectionNo']])) {
                    showMessage(message);
                    return;
                }
            }
        }
        await setTabWithRestrictionCheck(type);
        navigate(`/company-new/${company['_id']}#${type}`);
        setTimeout(() => {
            let element = document.getElementById(sectionNo);
            if (element) element.scrollIntoView({ behavior: 'instant' }, true);
        }, 10000);
    };

    const setReportTab = async (type) => {
        if (isRestrictedBlocked()) return;
        if (isPaid && !hasPurchased) {
            openPaidModulePurchaseModal();
            return;
        }
        if ((user['role'] == "Advisor" || user['role'] == "Investor") && (permissionLevel == 'DASHBOARD')) {
            showMessage('You have Dashboard access only!');
            return;
        }
        if (type == 'investor-lead') {
            getFitInvestorFirms();
            return;
        }
        setTabWithRestrictionCheck(type + '-report');
    };

    return (
        <div
            className='bg1 new-shadow df fdc jcsb pr'
            style={{ height: '200px', border: '1px solid #B5BFDE', borderRadius: '20px', padding: '1.25rem' }}
        >
            {(module?.menusConfig?.length > 0 || module?.reportMenu?.length > 0) && (
                <ModuleCardMenusNew
                    module={module}
                    onNavigateSection={navigateSection}
                    setReportTab={setReportTab}
                    setIsWorkflowReport={setIsWorkflowReport}
                    setCompanyDashboardTab={setCompanyDashboardTab}
                    setTabWithRestrictionCheck={setTabWithRestrictionCheck}
                    type={type}
                    setTab={setTab}
                    company={company}
                    seeMore={seeMore}
                    howItWorks={howItWorks}
                    menus={menus}
                    reportMenu={reportMenu}
                />
            )}

            <div className='df fdc jcsb' style={{ flex: 1 }}>

                {/* ── TOP: icon + name + subscription badge ───────────────── */}
                <ModuleCardHeader
                    resolvedLogo={resolvedLogo}
                    logoAlt={logoAlt}
                    subTitle={subTitle}
                    name={name}
                    setTab={setTabWithRestrictionCheck}
                    moduleSlug={moduleSlug}
                    company={company}
                    subscriptionLabel={subscriptionLabel}
                    isPaid={isPaid}
                    hasPurchased={hasPurchased}
                    isRestricted={isRestricted}
                    onRestrictedClick={showRestrictedMessage}
                    onRequirePurchase={openPaidModulePurchaseModal}
                    externalUrl={module?.externalUrl}
                    isResearchAid={module?.isResearchAid || false}
                    isNavigationModule={module?.isNavigationModule || false}
                    isDirectorsModule={module?.isDirectorsModule || false}
                    tempDirectorsModuleSlug={module?.slug || moduleSlug}
                />

                {/* ── BOTTOM: conditional on isStarted ────────────────────── */}
                <div className='w100 df aife jcsb' style={{ gap: '0.5rem' }}>

                    {/* LEFT — description (not started/purchased) | percentages (started/purchased) */}
                    {showStats ? (
                        <ModuleCardStats
                            showCompleteness={showCompleteness}
                            completenessPercent={completenessPercent}
                            showRisk={showRisk}
                            riskPercent={riskPercent}
                            isInvestorMatch={module?.isInvestorMatch || false}
                            strongMatchCount={module?.strongMatchCount || 0}
                            mediumMatchCount={module?.mediumMatchCount || 0}
                        />
                    ) : (
                        <ModuleCardDescription descriptionText={descriptionText} />
                    )}

                    {/* RIGHT — duration + buy (not started/purchased) | certification / pass-fail (started/purchased) */}
                    {showStats ? (
                        <div className='df aic jcfe' style={{ flexShrink: 0 }}>
                            {showCertification && (
                                <CertificationBadge moduleCertification={moduleCertification} />
                            )}
                            {showPassFail && !showCertification && (
                                <PassFailBadge
                                    resolvedLogo={resolvedLogo}
                                    logoAlt={logoAlt}
                                    hasPassed={hasPassed}
                                />
                            )}
                        </div>
                    ) : (
                        <ModuleCardActions durationLabel={durationLabel} isPaid={isPaid && !hasPurchased} onBuyClick={openPaidModulePurchaseModal} />
                    )}

                </div>
            </div>
        </div>
    );
}

export default ModuleCard;
